import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { scorecardService } from "@/modules/recruitment/infrastructure/scorecard-service"
import { ScorecardCriterionInput } from "@/modules/recruitment/entities/ScorecardTemplate"
import { ScorecardRatingInput } from "@/modules/recruitment/entities/ScorecardRating"
import { ScorecardRecommendation } from "@/modules/recruitment/enums/ScorecardRecommendation"

const templateQueryKey = (vacancyId: string) => ["scorecard-template", vacancyId]
const myScorecardQueryKey = (applicationId: string) => ["my-scorecard", applicationId]
const panelScorecardsQueryKey = (applicationId: string) => ["panel-scorecards", applicationId]

export function useScorecardTemplate(organizationId: string | undefined, vacancyId: string | undefined) {
  return useQuery({
    queryKey: templateQueryKey(vacancyId ?? ""),
    queryFn: () => scorecardService.getScorecardTemplate(organizationId!, vacancyId!),
    enabled: !!organizationId && !!vacancyId,
  })
}

export function useSaveScorecardTemplate(organizationId: string, vacancyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (criteria: ScorecardCriterionInput[]) =>
      scorecardService.saveScorecardTemplate(organizationId, vacancyId, criteria),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateQueryKey(vacancyId) })
      toast.success("Scorecard template saved")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save scorecard template")
    },
  })
}

export function useMyScorecard(organizationId: string | undefined, applicationId: string | undefined) {
  return useQuery({
    queryKey: myScorecardQueryKey(applicationId ?? ""),
    queryFn: () => scorecardService.getMyScorecard(organizationId!, applicationId!),
    enabled: !!organizationId && !!applicationId,
  })
}

export function useSaveScorecardDraft(organizationId: string, applicationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      interviewId?: string | null
      ratings: ScorecardRatingInput[]
      recommendation?: ScorecardRecommendation | null
      overallNotes?: string | null
    }) => scorecardService.saveScorecardDraft(organizationId, applicationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: myScorecardQueryKey(applicationId) })
      toast.success("Draft saved")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save draft")
    },
  })
}

export function useSubmitScorecard(organizationId: string, applicationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      interviewId?: string | null
      ratings: ScorecardRatingInput[]
      recommendation: ScorecardRecommendation
      overallNotes?: string | null
    }) => scorecardService.submitScorecard(organizationId, applicationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: myScorecardQueryKey(applicationId) })
      queryClient.invalidateQueries({ queryKey: panelScorecardsQueryKey(applicationId) })
      toast.success("Scorecard submitted")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to submit scorecard")
    },
  })
}

export function usePanelScorecards(organizationId: string | undefined, applicationId: string | undefined) {
  return useQuery({
    queryKey: panelScorecardsQueryKey(applicationId ?? ""),
    queryFn: () => scorecardService.getPanelScorecards(organizationId!, applicationId!),
    enabled: !!organizationId && !!applicationId,
  })
}
